import { businessToday, Decimal, getReasonCode, isReasonCode } from '@paguz/contracts';
import { Badge, cn, formatCop, MoneyText } from '@paguz/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api, queryKeys } from '../../api/endpoints.ts';
import type { AdminMetrics } from '../../api/metrics-schemas.ts';
import { ErrorState, LoadingRows, Panel } from '../../components/page.tsx';
import { formatBusinessDate, formatRate } from '../../lib/format.ts';
import { PHASE_LABEL } from '../../lib/labels.ts';
import { useCan } from '../../lib/session.ts';
import { useConsoleNow } from '../../lib/sla.ts';
import { UsagePanel } from '../agents/usage-panel.tsx';

const RANGES = [
  { key: 'today', label: 'Hoy', days: 0 },
  { key: '7d', label: '7 días', days: 7 },
  { key: '30d', label: '30 días', days: 30 },
] as const;
type RangeKey = (typeof RANGES)[number]['key'];

/** Desde: medianoche de Bogotá del día de negocio (hoy − N días). */
export function rangeFrom(now: Date, days: number): string {
  const start = new Date(now.getTime() - days * 86_400_000);
  return new Date(`${businessToday({ now: () => start })}T00:00:00-05:00`).toISOString();
}

/** KPI de compras (P0) y paneles del dashboard (`06-backoffice.md` §4.1). */
export function Dashboard() {
  const now = useConsoleNow();
  const [range, setRange] = useState<RangeKey>('today');
  const days = RANGES.find((r) => r.key === range)?.days ?? 0;
  const from = rangeFrom(now, days);
  const query = useQuery({
    queryKey: queryKeys.metrics(from),
    queryFn: () => api.getMetrics(from),
    refetchInterval: 30_000,
  });
  return (
    <section aria-label="Dashboard" className="mb-6 flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-semibold text-foreground">Indicadores</h2>
        <div
          role="radiogroup"
          aria-label="Rango de fechas"
          className="flex gap-1 rounded-xl bg-muted p-1"
        >
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              role="radio"
              aria-checked={range === r.key}
              onClick={() => {
                setRange(r.key);
              }}
              className={cn(
                'rounded-lg px-3 py-1.5 text-sm font-semibold text-muted-foreground',
                range === r.key && 'bg-background text-primary shadow-sm',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {query.isPending ? (
        <LoadingRows rows={4} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <Body metrics={query.data} />
      )}
    </section>
  );
}

function Body({ metrics: m }: { metrics: AdminMetrics }) {
  const completed = m.payments.byStatus.COMPLETED ?? 0;
  const ticket =
    completed > 0 ? new Decimal(m.payments.completedVolume.amount).div(completed).toFixed(2) : null;
  const canAgents = useCan('agents:read');
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Kpi
          label="Compras"
          value={<span>{completed}</span>}
          hint={`${m.payments.total} intenciones en el rango`}
        />
        <Kpi
          label="Volumen"
          value={<MoneyText value={m.payments.completedVolume.amount} size="xl" />}
          hint="Compras completadas"
        />
        <Kpi
          label="Ticket promedio"
          value={ticket ? <MoneyText value={ticket} size="xl" /> : '—'}
          hint="Volumen / compras"
        />
        <Kpi
          label="Tasa de éxito"
          value={
            m.payments.successRate === null
              ? '—'
              : formatRate(String(m.payments.successRate), '', 0)
          }
          hint="Completadas / (completadas + fallidas)"
        />
        <Kpi
          label="Pendientes"
          value={
            <span
              className={cn(m.payments.pendingConfirmation > 0 && 'text-warning-soft-foreground')}
            >
              {m.payments.pendingConfirmation}
            </span>
          }
          hint={
            m.payments.pendingConfirmation > 0
              ? 'En verificación con Bre-B'
              : 'Sin compras en verificación'
          }
          to
        />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        {m.daily && m.daily.length > 0 && (
          <Panel title="Volumen diario" description="Compras completadas por día (pesos)">
            <Bars
              rows={m.daily.map((d) => ({
                label: formatBusinessDate(d.date).replace(/ \d{4}$/, ''),
                value: new Decimal(d.volume.amount),
                text: `${formatCop(d.volume.amount)} · ${d.purchases} compras`,
              }))}
            />
          </Panel>
        )}
        {m.failuresByReason && (
          <Panel title="Fallas por código" description="Top 5 de reasonCode en compras fallidas">
            {m.failuresByReason.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin fallas en el rango.</p>
            ) : (
              <Bars
                rows={m.failuresByReason.map((f) => ({
                  label: f.reasonCode,
                  value: new Decimal(f.count),
                  text: `${f.count} · ${isReasonCode(f.reasonCode) ? getReasonCode(f.reasonCode).operatorDetail : ''}`,
                }))}
              />
            )}
          </Panel>
        )}
        {m.origination && (
          <Panel
            title="Originación"
            description={`Conversión ${m.origination.started ? formatRate(new Decimal(m.origination.active).div(m.origination.started).toFixed(4), '', 0) : '—'} · decisión en ${m.origination.avgHoursToDecision ?? '—'} h promedio`}
          >
            <Bars
              rows={m.origination.funnel.map((f) => ({
                label: PHASE_LABEL[f.phase] ?? f.phase,
                value: new Decimal(f.count),
                text: `${f.count} · ${f.avgHours} h en fase`,
              }))}
            />
            <p className="mt-3 text-sm text-muted-foreground">
              Aprobadas {m.origination.approved} · rechazadas {m.origination.rejected} ·
              contraofertas {m.origination.counterOffers}
            </p>
          </Panel>
        )}
        {m.portfolio && (
          <Panel
            title="Cartera"
            description={`Uso del cupo ${formatRate(m.portfolio.usageRate, '', 0)}`}
          >
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Cupo aprobado</dt>
                <dd>
                  <MoneyText
                    value={m.portfolio.approvedLimit.amount}
                    size="md"
                    className="font-semibold"
                  />
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Utilizado</dt>
                <dd>
                  <MoneyText
                    value={m.portfolio.usedLimit.amount}
                    size="md"
                    className="font-semibold"
                  />
                </dd>
              </div>
              {m.portfolio.delinquentBalance.map((b) => (
                <div key={b.bucket}>
                  <dt className="text-muted-foreground">Mora {b.bucket}</dt>
                  <dd>
                    <MoneyText value={b.amount.amount} size="sm" />
                  </dd>
                </div>
              ))}
            </dl>
          </Panel>
        )}
        {canAgents && <UsagePanel compact />}
        <Health
          openExceptions={m.reconciliation.openExceptions}
          critical={m.reconciliation.critical}
        />
      </div>
    </>
  );
}

function Kpi({
  label,
  value,
  hint,
  to,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
  to?: boolean;
}) {
  const body = (
    <>
      <p className="text-sm font-semibold text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-3xl font-semibold text-foreground tabular-nums [&_*]:font-display">
        {value}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </>
  );
  const cls = 'rounded-2xl border border-border bg-card p-5';
  return to ? (
    <Link
      to="/transacciones"
      search={{ status: 'PENDING_CONFIRMATION' }}
      className={cn(cls, 'transition-shadow hover:shadow-md')}
    >
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/** Barras horizontales de una sola serie (un tono; el valor va en texto, no en el color). */
function Bars({ rows }: { rows: readonly { label: string; value: Decimal; text: string }[] }) {
  const max = Decimal.max(...rows.map((r) => r.value), 1);
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((r) => (
        <li
          key={r.label}
          className="grid grid-cols-[8rem_minmax(0,1fr)] items-center gap-3 text-sm"
          title={`${r.label}: ${r.text}`}
        >
          <span className="truncate text-muted-foreground">{r.label}</span>
          <span className="flex items-center gap-2">
            <span
              className="h-3 rounded-r bg-highlight"
              style={{
                width: `${r.value.div(max).times(70).toNumber()}%`,
                minWidth: r.value.isZero() ? 0 : 4,
              }}
            />
            <span className="shrink-0 text-xs text-foreground tabular-nums">{r.text}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function Health({ openExceptions, critical }: { openExceptions: number; critical: number }) {
  const query = useQuery({
    queryKey: queryKeys.integrations,
    queryFn: api.getIntegrations,
    refetchInterval: 60_000,
  });
  return (
    <Panel
      title="Salud"
      description={`Excepciones de conciliación abiertas: ${openExceptions}${critical ? ` (${critical} críticas)` : ''}`}
    >
      {query.isPending ? (
        <LoadingRows rows={3} />
      ) : query.isError ? (
        <p className="text-sm text-muted-foreground">
          No se pudo consultar la salud de los adaptadores.
        </p>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {query.data.data.map((h) => (
            <li
              key={h.port}
              className="flex items-center justify-between gap-2 rounded-xl border border-border px-3 py-2 text-sm"
            >
              <span>
                <span className="font-semibold">{h.port.replace('Port', '')}</span>
                <span className="block text-xs text-muted-foreground">
                  {h.adapter}
                  {h.latencyMs !== undefined && ` · ${h.latencyMs} ms`}
                </span>
              </span>
              <Badge
                variant={
                  h.status === 'UP' ? 'success' : h.status === 'DEGRADED' ? 'warning' : 'danger'
                }
              >
                {h.status === 'UP' ? 'Operando' : h.status === 'DEGRADED' ? 'Degradado' : 'Caído'}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
